import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Target,
  Signal,
  ArrowRight,
  Check,
  Mail,
  Thermometer,
  Scale,
  Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import RobotoSlabBlack from "../fonts/RobotoSlab-Black.ttf";

// Hero background image
const HERO_BG =
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=80";

// Replace these with your real store links
const APP_STORE_URL = "https://apps.apple.com/app/idYOUR_APP_ID";
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=YOUR_PACKAGE_NAME";

// Equipment Grades
const PRICING_TIERS = [
  {
    id: "rambler",
    name: "Rambler",
    price: "0",
    currency: "£",
    period: "Free",
    description: "The Essentials",
    tagline: "For those who keep an eye on the horizon.",
    features: [
      "Weekly field notes digest",
      "Best routes from both daily runs",
      "Learn timing patterns as you go",
      "No commitment required",
    ],
    cta: "Start Here",
    link: "https://buy.stripe.com/8x2eV60fIfps3Ik3qde7m09",
    featured: false,
  },
  {
    id: "adventurer",
    name: "Adventurer",
    price: "3",
    currency: "£",
    period: "/month",
    description: "Full Access",
    tagline:
      "Daily alerts. Twice a day. Morning long-haul, evening short-haul.",
    features: [
      "Direct Telegram alerts (AM 7:30, PM 16:30 UTC)",
      "Morning run for big trips—Japan, Peru, Iceland",
      "Evening run for weekend breaks—Porto, Marrakech",
      "Booking links the moment routes go live",
      "Full scoring breakdown on every route",
    ],
    cta: "Full Access",
    link: "https://buy.stripe.com/3cI14g3rU4KOdiUbWJe7m08",
    featured: true,
  },
  {
    id: "nomad",
    name: "Nomad",
    price: "30",
    currency: "£",
    period: "/year",
    description: "The Complete Kit",
    tagline: "Same daily alerts. Annual price. Track patterns over 12 months.",
    features: [
      "Everything in Adventurer",
      "Works out to £2.50/month",
      "See seasonal windows open and close",
      "Historical benchmarks for route planning",
      "First consideration for route requests",
    ],
    cta: "Go Annual",
    link: "https://buy.stripe.com/9B67sE2nQa586Uw3qde7m07",
    featured: false,
  },
];

// Signal strength component
const SignalStrength = ({ strength }) => {
  const bars = [4, 8, 12, 16, 20];
  return (
    <div className="signal-bars" data-testid="signal-strength">
      {bars.map((height, idx) => (
        <div
          key={idx}
          className="signal-bar"
          style={{
            height: `${height}px`,
            opacity: idx < strength ? 1 : 0.2,
          }}
        />
      ))}
    </div>
  );
};

const AppleIcon = ({ className = "w-4 h-4 opacity-80" }) => (
  <svg
    className={className}
    viewBox="0 0 384 512"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M318.7 268.1c-.2-48.7 39.8-72 41.6-73.1-22.7-33.1-58-37.7-70.4-38.2-30-3-58.6 17.6-73.9 17.6-15.4 0-39.2-17.1-64.5-16.7-33.2.5-63.8 19.3-80.9 49.1-34.5 59.8-8.8 148.2 24.8 196.7 16.4 23.7 36 50.3 61.7 49.4 24.8-1 34.2-16 64.2-16 30 0 38.4 16 64.7 15.5 26.7-.5 43.6-24.2 59.9-47.9 18.8-27.4 26.6-53.9 27.1-55.3-.6-.3-52-19.9-52.3-79.1zM256 80c13.6-16.5 22.8-39.4 20.3-62.3-19.6.8-43.2 13.1-57.3 29.6-12.6 14.5-23.6 37.8-20.6 60 21.8 1.7 43.9-11.1 57.6-27.3z" />
  </svg>
);

const AndroidIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M7.523 7.756 5.88 5.006a.75.75 0 1 1 1.287-.77L8.9 7.132A7.23 7.23 0 0 1 12 6.45c1.106 0 2.155.247 3.1.682l1.734-2.896a.75.75 0 0 1 1.287.77l-1.643 2.75c1.66 1.082 2.772 2.885 2.932 4.956H4.59c.16-2.07 1.272-3.874 2.933-4.956ZM9.75 10.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm4.5 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM5.25 12.45h13.5v5.325A1.725 1.725 0 0 1 17.025 19.5H16.5v2.25a.75.75 0 0 1-1.5 0V19.5h-6v2.25a.75.75 0 0 1-1.5 0V19.5h-.525a1.725 1.725 0 0 1-1.725-1.725V12.45Z" />
  </svg>
);

function normalizeSignalVerdict(verdict) {
  const v = String(verdict || "").toLowerCase();

  if (
    v.includes("strong") ||
    v.includes("buy") ||
    v.includes("go") ||
    v.includes("book") ||
    v.includes("open")
  ) {
    return {
      dot: "text-emerald-400",
      text: "STRONG",
    };
  }

  if (
    v.includes("watch") ||
    v.includes("hold") ||
    v.includes("monitor") ||
    v.includes("trend")
  ) {
    return {
      dot: "text-amber-300",
      text: "WATCH",
    };
  }

  return {
    dot: "text-red-400",
    text: "WEAK",
  };
}

const STATIC_TICKER_ITEMS = [
  { id: "man-dxb", origin: "MAN", destination: "DXB", price: 289, verdict: "STRONG" },
  { id: "lgw-jfk", origin: "LGW", destination: "JFK", price: 379, verdict: "STRONG" },
  { id: "edi-bcn", origin: "EDI", destination: "BCN", price: 94,  verdict: "WATCH"  },
  { id: "lhr-nrt", origin: "LHR", destination: "NRT", price: 512, verdict: "STRONG" },
  { id: "man-kef", origin: "MAN", destination: "KEF", price: 118, verdict: "STRONG" },
  { id: "lgw-lim", origin: "LGW", destination: "LIM", price: 447, verdict: "WATCH"  },
  { id: "lhr-bkk", origin: "LHR", destination: "BKK", price: 398, verdict: "STRONG" },
  { id: "bhx-opo", origin: "BHX", destination: "OPO", price: 67,  verdict: "WATCH"  },
  { id: "gla-mad", origin: "GLA", destination: "MAD", price: 103, verdict: "STRONG" },
  { id: "lhr-mex", origin: "LHR", destination: "MEX", price: 489, verdict: "WATCH"  },
  { id: "man-cph", origin: "MAN", destination: "CPH", price: 82,  verdict: "STRONG" },
  { id: "lgw-cmn", origin: "LGW", destination: "CMN", price: 134, verdict: "STRONG" },
];

function LiveSignalsTicker() {
  const repeated = [...STATIC_TICKER_ITEMS, ...STATIC_TICKER_ITEMS];
  return (
    <div className="mb-8 border-y border-white/10 bg-stone-950/70">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 py-2 border-b border-white/5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">
          <span className="text-emerald-400 animate-pulse">●</span>
          <span>Recent Signals</span>
        </div>
        <a href="#equipment" className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-gold hover:text-brand-bone transition-colors">
          Full board lives in the app
        </a>
      </div>
      <div className="overflow-hidden">
        <div className="ticker-track flex w-max items-center gap-10 whitespace-nowrap px-4 py-3">
          {repeated.map((item, idx) => {
            const signal = normalizeSignalVerdict(item.verdict);
            return (
              <div key={`${item.id}-${idx}`} className="flex items-center gap-2 font-mono text-xs">
                <span className={`${signal.dot} text-[12px]`}>●</span>
                <span className="text-stone-300">{item.origin} → {item.destination}</span>
                <span className="text-brand-gold">£{item.price}</span>
                <span className={`${signal.dot} text-[10px] tracking-[0.18em]`}>{signal.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Deal Card
const DealCard = ({ deal, index }) => {
  const themeColors = {
    "Luxury Value": "bg-amber-900/20 text-amber-300 border-amber-600/20",
    "Northern Lights": "bg-emerald-900/20 text-emerald-300 border-emerald-600/20",
    "Cultural Peak": "bg-purple-900/20 text-purple-300 border-purple-600/20",
    Snow: "bg-sky-900/20 text-sky-300 border-sky-600/20",
    Adventure: "bg-orange-900/20 text-orange-300 border-orange-600/20",
  };

  return (
    <div
      className="deal-card bg-stone-900/50 border border-white/10 p-6 opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex justify-between items-start mb-4">
        <Badge
          variant="outline"
          className={`font-mono text-xs ${
            themeColors[deal.theme] || themeColors["Adventure"]
          }`}
        >
          {deal.theme}
        </Badge>
        <SignalStrength strength={deal.signal_strength} />
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-barlow font-bold text-3xl text-brand-bone tracking-tight">
            {deal.origin}
          </span>
          <ArrowRight className="w-4 h-4 text-stone-500" />
          <span className="font-barlow font-bold text-3xl text-brand-bone tracking-tight">
            {deal.destination}
          </span>
        </div>
        <div className="font-mono text-sm text-stone-400">
          {deal.outbound_date} → {deal.return_date}
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-stone-500 mb-1">from</div>
          <div className="font-barlow font-bold text-4xl text-brand-gold tracking-tight">
            £{deal.price}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-xs text-stone-500 mb-1">score</div>
          <div className="font-mono text-lg text-stone-300">
            {deal.rigor_score}/10
          </div>
        </div>
      </div>
    </div>
  );
};

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTier, setActiveTier] = useState("adventurer");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Invisible bot hardening (does NOT change UI)
  const [website, setWebsite] = useState("");
  const [formShownAt] = useState(() => Date.now());

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setIsLoading(true);

        const [dealsResult] = await Promise.allSettled([
          fetch(`/deals.json?t=${Date.now()}`).then((r) => r.json()),
        ]);

        if (dealsResult.status === "fulfilled") {
          const payload = dealsResult.value;
          const list = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.deals)
            ? payload.deals
            : [];

          setDeals(list);
        } else {
          console.error("Error fetching deals:", dealsResult.reason);
          setDeals([]);
        }

      } catch (error) {
        console.error("Error loading landing data:", error);
        setDeals([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeals();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website, formShownAt }),
      });

      if (res.ok) {
        toast.success("You're in. First field notes arrive Sunday.");
        setEmail("");
        setWebsite("");
      } else {
        toast.error("That didn't work. Try again?");
      }
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error("That didn't work. Try again?");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTierSelect = (tierId) => {
    setActiveTier(tierId);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <style>{`
        @font-face {
          font-family: 'RobotoSlab';
          src: url(${RobotoSlabBlack}) format('truetype');
          font-weight: 900;
          font-style: normal;
        }

        .font-robotoslab-black {
          font-family: 'RobotoSlab', serif;
          font-weight: 900;
        }

        @keyframes signalScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .ticker-track {
          animation: signalScroll 34s linear infinite;
          will-change: transform;
        }

        .ticker-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-robotoslab-black text-lg tracking-tight text-brand-bone">
              TRAVELTXTER
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a
              href="#curation"
              className="font-mono text-xs uppercase tracking-wider text-stone-400 hover:text-brand-gold transition-colors"
            >
              Curation
            </a>
            <a
              href="#standard"
              className="font-mono text-xs uppercase tracking-wider text-stone-400 hover:text-brand-gold transition-colors"
            >
              Our Standard
            </a>
            <a
              href="#equipment"
              className="font-mono text-xs uppercase tracking-wider text-stone-400 hover:text-brand-gold transition-colors"
            >
              Equipment
            </a>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Download on the App Store"
              className="text-stone-400 hover:text-brand-gold transition-colors"
            >
              <AppleIcon className="w-4 h-4" />
            </a>

            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Get it on Google Play"
              className="text-stone-400 hover:text-brand-gold transition-colors"
            >
              <AndroidIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="relative min-h-[85vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-black/40 backdrop-blur-sm border border-white/10 mb-8">
            <Signal className="w-4 h-4 text-brand-gold" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-stone-300">
              A Field Guide for the Skies
            </span>
          </div>

          <h1 className="font-barlow font-extrabold text-5xl md:text-7xl lg:text-8xl tracking-tight text-brand-bone mb-6 leading-[1.1]">
            Go more places...
          </h1>

          <p className="text-stone-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-6">
            We filter out the noise. No 12-hour layovers masquerading as deals.
            No clickbait urgency. Just honest travel opportunities worth
            considering, curated for people who actually go places.
          </p>

          <p className="text-stone-500 text-sm font-mono uppercase tracking-[0.18em] mb-10">
            Now available on iOS and Android
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-brand-gold text-black hover:bg-brand-gold/90 font-mono text-xs uppercase tracking-wider px-8 py-6"
              onClick={() =>
                document
                  .getElementById("equipment")
                  .scrollIntoView({ behavior: "smooth" })
              }
            >
              View Equipment
            </Button>
            <Button
              variant="outline"
              className="border-white/20 text-brand-bone hover:bg-white/10 font-mono text-xs uppercase tracking-wider px-8 py-6"
              onClick={() =>
                document
                  .getElementById("curation")
                  .scrollIntoView({ behavior: "smooth" })
              }
            >
              Today&apos;s Curation
            </Button>
          </div>
        </div>
      </section>

      {/* Today's Curation */}
      <section id="curation" className="py-24 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Signal className="w-5 h-5 text-brand-gold" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
                Today&apos;s Curation
              </span>
            </div>

            <h2 className="font-barlow font-extrabold text-4xl md:text-5xl tracking-tight text-brand-bone mb-4">
              The Current Selection
            </h2>

            <p className="text-stone-400 leading-relaxed">
              Routes that passed our filter. Each one vetted for timing, value,
              and connection quality. Our Telegram members saw these first. Some
              runs find nothing worth publishing. That&apos;s the standard working
              as intended.
            </p>
          </div>

          {!isLoading && (
            <LiveSignalsTicker />
          )}

          {isLoading ? (
            <div className="text-stone-400 font-mono">
              Loading current curation…
            </div>
          ) : deals.length === 0 ? (
            <div className="text-stone-400 font-mono">
              Nothing this run. The filter held.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {deals.slice(0, 9).map((deal, idx) => (
                <DealCard key={deal.id || idx} deal={deal} index={idx} />
              ))}
            </div>
          )}
          {/* Powered by MIZAR */}
          {!isLoading && (
            <div className="mt-16 pt-10 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-600">
                  Pricing intelligence
                </span>
                <span className="text-stone-700">·</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-600">
                  Powered by
                </span>
                <a
                  href="https://mizar.traveltxter.com"
                  target="_blank"
                  rel="noreferrer"
                  className="font-robotoslab-black text-sm tracking-tight text-stone-500 hover:text-brand-gold transition-colors"
                >
                  MIZAR
                </a>
              </div>
              <a
                href="https://mizar.traveltxter.com"
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-600 hover:text-brand-gold transition-colors"
              >
                B2B API access →
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Our Standard */}
      <section
        id="standard"
        className="py-24 bg-stone-950 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-5 h-5 text-brand-gold" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
                Our Standard
              </span>
            </div>

            <h2 className="font-barlow font-extrabold text-4xl md:text-5xl tracking-tight text-brand-bone mb-4">
              The Traveltxter Standard
            </h2>

            <p className="text-stone-400 leading-relaxed">
              We&apos;re not trying to hack travel. We&apos;re respecting your time by
              providing better tools. Every route here has passed three filters.
              Twice a day we run searches from UK airports. Morning for long-haul.
              Evening for short-haul. What doesn&apos;t pass doesn&apos;t publish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-black/40 border border-white/10 p-8">
              <div className="font-mono text-xs uppercase tracking-wider text-brand-gold mb-2">
                The First Test
              </div>
              <Thermometer className="w-6 h-6 text-brand-gold mb-4" />
              <h3 className="font-barlow font-bold text-xl text-brand-bone mb-3">
                The Value Filter
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                We compare every fare against years of data for that route. Our
                Value Score benchmarks prices against historical patterns. If
                the numbers don&apos;t hold up, it doesn&apos;t make the cut.
              </p>
            </div>

            <div className="bg-black/40 border border-white/10 p-8">
              <div className="font-mono text-xs uppercase tracking-wider text-brand-gold mb-2">
                The Second Test
              </div>
              <Scale className="w-6 h-6 text-brand-gold mb-4" />
              <h3 className="font-barlow font-bold text-xl text-brand-bone mb-3">
                Seasonal Intelligence
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                We track when places are actually worth visiting. Northern Lights
                in February. Cherry blossoms in April. Shoulder seasons when
                weather holds. Routes surface when destinations are at their best.
              </p>
            </div>

            <div className="bg-black/40 border border-white/10 p-8">
              <div className="font-mono text-xs uppercase tracking-wider text-brand-gold mb-2">
                The Third Test
              </div>
              <Clock className="w-6 h-6 text-brand-gold mb-4" />
              <h3 className="font-barlow font-bold text-xl text-brand-bone mb-3">
                Connection Quality
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                A cheap flight with a 14-hour layover isn&apos;t a deal—it&apos;s
                punishment. We prioritize direct routes and sensible connections.
                Your vacation time has value. We treat it accordingly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Grades */}
      <section id="equipment" className="py-24 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-5 h-5 text-brand-gold" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
                Equipment Grades
              </span>
            </div>

            <h2 className="font-barlow font-extrabold text-4xl md:text-5xl tracking-tight text-brand-bone mb-4">
              Select Your Kit
            </h2>

            <p className="text-stone-400 leading-relaxed">
              Different travelers need different tools. Choose the access level
              that fits how you move. All tiers get the same scoring system. All
              tiers get honest curation. The difference is timing and delivery
              method.
            </p>
          </div>

          <div className="mb-8 border border-white/10 bg-stone-950/60 p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-2xl">
                <div className="font-mono text-xs uppercase tracking-wider text-brand-gold mb-2">
                  Traveltxter App
                </div>
                <h3 className="font-barlow font-bold text-2xl md:text-3xl text-brand-bone mb-3">
                  The Field Guide
                </h3>
                <p className="text-stone-400 text-sm md:text-base leading-relaxed">
                  Browse curated routes, track destinations, and discover where
                  your budget can take you. The Traveltxter field guide now
                  lives in your pocket.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-3 border border-white/10 bg-white/5 px-5 py-4 text-brand-bone hover:bg-white/10 transition-colors min-w-[220px]"
                >
                  <AppleIcon className="w-5 h-5" />
                  <span className="font-mono text-xs uppercase tracking-wider">
                    App Store
                  </span>
                </a>

                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-3 border border-white/10 bg-white/5 px-5 py-4 text-brand-bone hover:bg-white/10 transition-colors min-w-[220px]"
                >
                  <AndroidIcon className="w-5 h-5" />
                  <span className="font-mono text-xs uppercase tracking-wider">
                    Android
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`border p-8 cursor-pointer transition-all ${
                  activeTier === tier.id
                    ? "border-brand-gold bg-stone-950/60"
                    : "border-white/10 bg-black/40 hover:border-white/20"
                }`}
                onClick={() => handleTierSelect(tier.id)}
              >
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="font-barlow font-bold text-2xl text-brand-bone mb-1">
                      {tier.name}
                    </h3>
                    <div className="font-mono text-xs uppercase tracking-wider text-stone-400">
                      {tier.description}
                    </div>
                  </div>
                  {tier.featured && (
                    <Badge className="bg-brand-gold text-black font-mono text-xs uppercase tracking-wider">
                      Recommended
                    </Badge>
                  )}
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="font-barlow font-extrabold text-5xl text-brand-gold">
                      {tier.currency}
                      {tier.price}
                    </span>
                    <span className="text-stone-400 font-mono text-sm">
                      {tier.period}
                    </span>
                  </div>
                  <p className="text-stone-400 text-sm mt-2">{tier.tagline}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" />
                      <span className="text-stone-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full font-mono text-xs uppercase tracking-wider py-6 ${
                    tier.featured
                      ? "bg-brand-gold text-black hover:bg-brand-gold/90"
                      : "bg-white/5 text-brand-bone border border-white/10 hover:bg-white/10"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = tier.link;
                  }}
                >
                  {tier.cta}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-20 flex justify-center">
            <div className="w-full max-w-2xl bg-stone-950/60 border border-white/10 p-8">
              <div className="flex items-center gap-3 mb-4 justify-center">
                <Mail className="w-5 h-5 text-brand-gold" />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
                  The Field Notes
                </span>
              </div>

              <h3 className="font-barlow font-bold text-2xl text-brand-bone mb-3 text-center">
                Receive the Field Notes
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed mb-6 text-center">
                Every Sunday. The week&apos;s best routes, seasonal windows opening,
                and what we&apos;re tracking. No spam. Just signal.
              </p>

              <form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-3"
              >
                <div
                  style={{ position: "absolute", left: "-5000px" }}
                  aria-hidden="true"
                >
                  <input
                    type="text"
                    name="website"
                    tabIndex="-1"
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-black/40 border-white/10 text-brand-bone placeholder:text-stone-600"
                  disabled={isSubmitting}
                  required
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-brand-gold text-black hover:bg-brand-gold/90 font-mono text-xs uppercase tracking-wider px-8 py-6 disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? "Adding..." : "Receive Notes"}
                </Button>
              </form>

              <p className="text-stone-500 text-xs text-center mt-4 font-mono">
                Sent Sundays. Unsubscribe anytime. No noise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="font-robotoslab-black text-lg tracking-tight text-brand-bone">
              TRAVELTXTER
            </span>
          </div>

          <div className="flex items-center gap-5">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Download on the App Store"
              className="text-stone-500 hover:text-brand-gold transition-colors"
            >
              <AppleIcon className="w-5 h-5" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Get it on Google Play"
              className="text-stone-500 hover:text-brand-gold transition-colors"
            >
              <AndroidIcon className="w-5 h-5" />
            </a>
            <div className="text-stone-500 font-mono text-xs">
              The pursuit of better departures.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
